import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetEndoFamiliesQuery } from "store/queries/professionalRuleset";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ToggleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ToggleLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
`;

const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ToggleHelp = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const FamilySection = styled.div<{ $disabled: boolean }>`
  margin-top: 1rem;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  padding: 1rem;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
`;

const FamilySectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const FamilyList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid var(--gray-100);
  border-radius: 6px;
`;

const FamilyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0.75rem;

  &:not(:last-child) {
    border-bottom: 1px solid var(--gray-100);
  }
`;

const FamilyLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const FamilyMembers = styled.span`
  font-size: 0.8rem;
  color: var(--gray-600);
`;

const EmptyFamilies = styled.div`
  padding: 0.75rem;
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

export interface IProfessionalRulesetFormData {
  providerId: string | null;
  payerId: number | null;
  compoundBilateralAndMppr: boolean;
  endoApplies: boolean;
  disabledEndoBaseCptCodes: string[];
}

export const INITIAL_PROFESSIONAL_FORM_DATA: IProfessionalRulesetFormData = {
  providerId: null,
  payerId: null,
  compoundBilateralAndMppr: true,
  endoApplies: true,
  disabledEndoBaseCptCodes: [],
};

interface IProfessionalRulesetFormProps {
  title: string;
  submitLabel: string;
  formData: IProfessionalRulesetFormData;
  onChange: (data: IProfessionalRulesetFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /**
   * In edit mode, scope (provider+payer) is the composite key and is locked.
   */
  scopeLocked?: boolean;
}

export function ProfessionalRulesetForm({
  title,
  submitLabel,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  scopeLocked,
}: IProfessionalRulesetFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();
  const { data: endoFamiliesData, isLoading: isLoadingFamilies } =
    useGetEndoFamiliesQuery();
  const [familyFilter, setFamilyFilter] = useState("");

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    if (!payers?.data) return [];
    return payers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [payers]);

  const families = useMemo(
    () => endoFamiliesData?.data ?? [],
    [endoFamiliesData]
  );

  const disabledSet = useMemo(
    () => new Set(formData.disabledEndoBaseCptCodes),
    [formData.disabledEndoBaseCptCodes]
  );

  const filteredFamilies = useMemo(() => {
    const term = familyFilter.trim().toLowerCase();
    if (!term) return families;
    return families.filter(
      (f) =>
        f.baseCptCode.toLowerCase().includes(term) ||
        f.cptCodes.some((c) => c.toLowerCase().includes(term))
    );
  }, [families, familyFilter]);

  const enabledFamilyCount = families.length - disabledSet.size;

  const handleChange = (
    field: keyof IProfessionalRulesetFormData,
    value: any
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const setFamilyEnabled = (baseCptCode: string, enabled: boolean) => {
    const next = new Set(formData.disabledEndoBaseCptCodes);
    if (enabled) {
      next.delete(baseCptCode);
    } else {
      next.add(baseCptCode);
    }
    handleChange("disabledEndoBaseCptCodes", Array.from(next));
  };

  const setAllFamilies = (enabled: boolean) => {
    handleChange(
      "disabledEndoBaseCptCodes",
      enabled ? [] : families.map((f) => f.baseCptCode)
    );
  };

  const isValid =
    formData.providerId !== null && formData.payerId !== null && !isSubmitting;

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
          aria-label="Back"
        />
        <h2 className="m-0">{title}</h2>
      </div>

      <Card className="mb-4">
        <FieldGrid>
          <Field>
            <label htmlFor="providerId">Provider</label>
            <Dropdown
              inputId="providerId"
              value={formData.providerId}
              options={providerOptions}
              onChange={(e) => handleChange("providerId", e.value)}
              placeholder="Select a provider"
              filter
              disabled={scopeLocked}
            />
          </Field>
          <Field>
            <label htmlFor="payerId">Payer</label>
            <Dropdown
              inputId="payerId"
              value={formData.payerId}
              options={payerOptions}
              onChange={(e) => handleChange("payerId", e.value)}
              placeholder="Select a payer"
              filter
              disabled={scopeLocked}
            />
          </Field>
        </FieldGrid>

        <ToggleRow>
          <ToggleLine>
            <ToggleLabel>
              <strong>Compound Bilateral and MPPR</strong>
              <ToggleHelp>
                When off, MPPR is not stacked on top of bilateral discounts. UHC
                professional MPPR policy expects this off.
              </ToggleHelp>
            </ToggleLabel>
            <InputSwitch
              checked={formData.compoundBilateralAndMppr}
              onChange={(e) =>
                handleChange("compoundBilateralAndMppr", e.value ?? false)
              }
            />
          </ToggleLine>
          <ToggleLine>
            <ToggleLabel>
              <strong>Endoscopy Rules Apply</strong>
              <ToggleHelp>
                Master switch. When off, all endoscopy adjustments are skipped
                for this provider and payer; codes flow through bilateral and
                MPPR like any other.
              </ToggleHelp>
            </ToggleLabel>
            <InputSwitch
              checked={formData.endoApplies}
              onChange={(e) => handleChange("endoApplies", e.value ?? false)}
            />
          </ToggleLine>
        </ToggleRow>

        <FamilySection $disabled={!formData.endoApplies}>
          <FamilySectionHeader>
            <ToggleLabel>
              <strong>Endoscopy Families</strong>
              <ToggleHelp>
                Turn individual endoscopy families on or off. A disabled family
                is skipped during endoscopy adjustments; its codes still flow
                through bilateral and MPPR.
                {families.length > 0 &&
                  ` ${enabledFamilyCount} of ${families.length} on.`}
              </ToggleHelp>
            </ToggleLabel>
            <div className="flex gap-2">
              <Button
                label="Enable all"
                className="p-button-text p-button-sm"
                type="button"
                onClick={() => setAllFamilies(true)}
                disabled={families.length === 0}
              />
              <Button
                label="Disable all"
                className="p-button-text p-button-sm"
                type="button"
                onClick={() => setAllFamilies(false)}
                disabled={families.length === 0}
              />
            </div>
          </FamilySectionHeader>

          <span className="p-input-icon-left mb-2 block">
            <i className="pi pi-search" />
            <InputText
              className="w-full"
              value={familyFilter}
              onChange={(e) => setFamilyFilter(e.target.value)}
              placeholder="Filter by base or member CPT code"
            />
          </span>

          <FamilyList>
            {isLoadingFamilies && (
              <EmptyFamilies>Loading endoscopy families…</EmptyFamilies>
            )}
            {!isLoadingFamilies && families.length === 0 && (
              <EmptyFamilies>No endoscopy families found.</EmptyFamilies>
            )}
            {!isLoadingFamilies &&
              families.length > 0 &&
              filteredFamilies.length === 0 && (
                <EmptyFamilies>
                  No families match &ldquo;{familyFilter}&rdquo;.
                </EmptyFamilies>
              )}
            {filteredFamilies.map((family) => (
              <FamilyRow key={family.baseCptCode}>
                <FamilyLabel>
                  <strong>{family.baseCptCode}</strong>
                  <FamilyMembers>
                    {family.cptCodes.length} member
                    {family.cptCodes.length === 1 ? "" : "s"}
                    {family.alsoAllowMpdr ? " · also allows MPDR" : ""}
                  </FamilyMembers>
                </FamilyLabel>
                <InputSwitch
                  checked={!disabledSet.has(family.baseCptCode)}
                  onChange={(e) =>
                    setFamilyEnabled(family.baseCptCode, e.value ?? false)
                  }
                  aria-label={`Endoscopy family ${family.baseCptCode}`}
                />
              </FamilyRow>
            ))}
          </FamilyList>
        </FamilySection>

        <Footer>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => navigate("/payer-rules")}
          />
          <Button
            label={submitLabel}
            icon="pi pi-check"
            onClick={onSubmit}
            disabled={!isValid}
            loading={isSubmitting}
          />
        </Footer>
      </Card>
    </div>
  );
}
